'use client';

/**
 * "Was this helpful?" widget for help-center articles.
 *
 * Persists the vote to `POST /api/help/feedback` (writes a
 * `cx_help_feedback` row; anonymous submissions are allowed by design).
 * "Yes" submits immediately; "No" reveals an optional one-line reason
 * box and submits on Send/Skip so the not-helpful signal carries a
 * cause when the reader offers one. Fire-and-forget: a failed write is
 * never surfaced to the reader — the thank-you shows regardless.
 */
import { useState } from 'react';

const MONO_LABEL: React.CSSProperties = {
  fontFamily: 'var(--font-geist-mono)',
  fontSize: '11px',
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: 'var(--dimmer)',
};

const PILL_BUTTON: React.CSSProperties = {
  padding: '6px 12px',
  background: 'transparent',
  border: '1px solid var(--hair-2)',
  borderRadius: '8px',
  color: 'var(--paper)',
  cursor: 'pointer',
  fontFamily: 'var(--font-geist-sans)',
  fontSize: '13px',
};

interface Props {
  /** Article slug, e.g. `getting-started/welcome`. */
  kbSlug: string;
  /** Article category, e.g. `getting-started`. */
  kbCategory: string;
}

export function HelpFeedback({ kbSlug, kbCategory }: Props): React.JSX.Element {
  const [stage, setStage] = useState<'idle' | 'reason' | 'done'>('idle');
  const [sentiment, setSentiment] = useState<'helpful' | 'not_helpful' | null>(null);
  const [comment, setComment] = useState('');

  async function submit(value: 'helpful' | 'not_helpful', note?: string): Promise<void> {
    try {
      await fetch('/api/help/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kb_slug: kbSlug,
          kb_category: kbCategory,
          sentiment: value,
          comment: note?.trim() ? note.trim() : null,
        }),
      });
    } catch {
      // Feedback is non-critical — never block the reader on a failed write.
    }
  }

  function onYes(): void {
    setSentiment('helpful');
    setStage('done');
    void submit('helpful');
  }

  function onNo(): void {
    setSentiment('not_helpful');
    setStage('reason');
  }

  function onSubmitReason(): void {
    setStage('done');
    void submit('not_helpful', comment);
  }

  if (stage === 'done') {
    return (
      <p style={{ ...MONO_LABEL, color: 'var(--dim)', margin: '32px 0 0' }}>
        {sentiment === 'helpful'
          ? 'Thanks for the feedback.'
          : 'Thanks — we will improve this article.'}
      </p>
    );
  }

  if (stage === 'reason') {
    return (
      <div style={{ margin: '32px 0 0', display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '460px' }}>
        <span style={MONO_LABEL}>{'What was missing? (optional)'}</span>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={2}
          maxLength={2000}
          aria-label="What was missing from this article?"
          style={{
            background: 'transparent',
            border: '1px solid var(--hair-2)',
            borderRadius: '8px',
            color: 'var(--paper)',
            fontFamily: 'var(--font-geist-sans)',
            fontSize: '14px',
            padding: '8px 10px',
            resize: 'vertical',
          }}
        />
        <div style={{ display: 'flex', gap: '12px' }}>
          <button type="button" onClick={onSubmitReason} style={PILL_BUTTON}>
            {'Send feedback'}
          </button>
          <button type="button" onClick={onSubmitReason} style={{ ...PILL_BUTTON, color: 'var(--dim)' }}>
            {'Skip'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ margin: '32px 0 0', display: 'flex', gap: '12px', alignItems: 'center' }}>
      <span style={MONO_LABEL}>{'Was this helpful?'}</span>
      <button type="button" onClick={onYes} style={PILL_BUTTON} aria-label="Yes, this article was helpful">
        {'Yes'}
      </button>
      <button type="button" onClick={onNo} style={PILL_BUTTON} aria-label="No, this article was not helpful">
        {'No'}
      </button>
    </div>
  );
}
